import {
    Breadcrumbs,
    CardContent,
    Fab,
    Grid,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TablePagination,
    TextField,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import MainCard from '../MainCard';
import { useDispatch, useSelector } from 'react-redux';
import EnhancedTableHead from './ListingTableHead';
import SkeletonDocument from 'ui-component/skeleton/SkeletonDocuments';
import ListingTableRow from './ListingTableRow';
import EmptyContent from 'ui-component/extended/EmptyContent';
import useTable, { getComparator } from 'hooks/useTable';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import useDebounce from 'hooks/useDebounce';
import AddIcon from '@mui/icons-material/AddTwoTone';
import AddManagers from './AddManagers';
import { EmployeeActions } from 'store/slices/employees';
import { roles } from 'store/constant';
import { openSnackbar } from 'store/slices/snackbar';
import { openConfirmationModal } from 'store/slices/confirmationModal';
import { applySortFilter } from 'views/attendance/leave';

const AmManageUsers = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { managers, loading, employee, totalData } = useSelector((state) => state.employee);
    const { getMyApartmentManagers, deleteApartmentManagers, fetchEmployees } = EmployeeActions;

    const theme = useTheme();
    const [tableData, setTableData] = useState([]);
    const [search, setSearch] = useState(null);

    const [openDialog, setOpenDialog] = useState(false);

    const {
        page,
        order,
        orderBy,
        rowsPerPage,
        setPage,
        selected,
        setSelected,
        onSelectRow,
        onSelectAllRows,
        onSort,
        onChangePage,
        onChangeRowsPerPage
    } = useTable();

    const debouncedSearch = useDebounce(search, 500);

    useEffect(() => {
        dispatch(getMyApartmentManagers({ page: page + 1, limit: rowsPerPage, search }));
    }, [page, rowsPerPage, debouncedSearch]);

    useEffect(() => {
        setTableData(managers);
    }, [managers]);

    const handleDelete = async (id) => {
        try {
            dispatch(
                openConfirmationModal({
                    open: true,
                    message: 'This action is not reversible.',
                    handleConfirm: async () => {
                        const res = await dispatch(deleteApartmentManagers(id));

                        if (res?.payload?.status !== 201 && res?.payload?.status !== 200) {
                            dispatch(
                                openSnackbar({
                                    open: true,
                                    message: res?.payload?.message || 'Internal server error',
                                    variant: 'alert',
                                    alert: {
                                        color: 'error'
                                    },
                                    close: true
                                })
                            );
                        } else {
                            dispatch(
                                openSnackbar({
                                    open: true,
                                    message: 'Manager removed successfully',
                                    variant: 'alert',
                                    alert: {
                                        color: 'success'
                                    },
                                    close: true
                                })
                            );

                            dispatch(getMyApartmentManagers({ page: page + 1, limit: rowsPerPage, search }));
                        }
                    }
                })
            );
        } catch (err) {
            console.error(err);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const dataFiltered = applySortFilter({
        tableData,
        comparator: getComparator(order, orderBy)
    });

    return (
        <>
            <Breadcrumbs heading="Add Managers" links={[{ name: 'Apartment Managers', href: '/ams/manage-users' }]} />
            <MainCard title="Add Managers">
                <CardContent>
                    <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Stack direction="row" gap={2} alignItems="center" sx={{ width: '100%' }}>
                                {/* Search TextField */}
                                <TextField
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" sx={{ color: '#462F4D' }} />
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            borderRadius: '8px', // Rounded corners
                                            padding: '8px' // Add some padding to improve spacing
                                        }
                                    }}
                                    fullWidth
                                    variant="outlined"
                                    size="small" // To reduce the size of the input
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search By Name"
                                />
                            </Stack>
                        </Grid>

                        <Grid item xs={12} md={4} gap={1} sx={{ textAlign: 'right' }}>
                            <Stack direction="row" gap={1} flexWrap="wrap" justifyContent="flex-end" alignItems="center">
                                {/* product add & dialog */}
                                <Tooltip title="Add New Manager">
                                    <Fab
                                        size="small"
                                        variant="extended"
                                        onClick={() => setOpenDialog(true)}
                                        sx={{
                                            boxShadow: 'none',
                                            ml: 1,
                                            p: 1,
                                            minHeight: 32,
                                            color: '#fff',
                                            bgcolor: theme.palette.secondary.main,
                                            '&:hover': { bgcolor: theme.palette.secondary.dark }
                                        }}
                                    >
                                        <AddIcon fontSize="small" />
                                        Add Manager
                                    </Fab>
                                </Tooltip>
                            </Stack>
                        </Grid>

                        <AddManagers openDialog={openDialog} handleCloseDialog={handleCloseDialog} />
                    </Grid>
                </CardContent>

                <TableContainer sx={{ mt: 3 }}>
                    <Table>
                        <EnhancedTableHead
                            numSelected={selected?.length}
                            order={order}
                            orderBy={orderBy}
                            setSelected={setSelected}
                            onSelectAllClick={(checked) =>
                                onSelectAllRows(
                                    checked,
                                    tableData?.map((row) => row?.id)
                                )
                            }
                            selected={selected}
                            onSort={onSort}
                            rowCount={tableData?.length}
                            theme={theme}
                        />
                        <TableBody>
                            {loading
                                ? [1, 2, 3, 4, 5].map((ele, index) => <SkeletonDocument key={ele + index} />)
                                : dataFiltered?.map((row, index) => (
                                      <ListingTableRow
                                          key={row?.apartmentManager?.id}
                                          row={{ ...row?.apartmentManager, status: row?.status }}
                                          index={page * rowsPerPage + index}
                                          selected={selected}
                                          onSelectRow={onSelectRow}
                                          handleDelete={handleDelete}
                                      />
                                  ))}
                        </TableBody>
                    </Table>
                    {!loading && tableData?.length === 0 && <EmptyContent title="No Managers found!" />}
                    <TablePagination
                        rowsPerPageOptions={[25, 50, 100]}
                        component="div"
                        count={totalData}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={onChangePage}
                        onRowsPerPageChange={onChangeRowsPerPage}
                    />
                </TableContainer>
            </MainCard>
        </>
    );
};

export default AmManageUsers;
